import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, handleToolError, maybeShow3DayOffer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ThreeDayOfferModal } from "@/components/ThreeDayOfferModal";
import { TrialLimitModal } from "@/components/TrialLimitModal";

const QUICK = [
  "Motor listrik termurah",
  "Hemat vs bensin per bulan",
  "Motor listrik jarak terjauh",
  "Sepeda listrik untuk harian",
];

export function AiAsk() {
  const navigate = useNavigate();
  const { user, isPremium, checkAuth } = useAuth();
  const [q, setQ] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [oneLeft, setOneLeft] = useState(false);

  useEffect(() => {
    if (user) api.get("/ai/history").then((r) => setHistory(r.data.items || [])).catch(() => {});
  }, [user]);

  const submit = async (question) => {
    const text = (question || "").trim();
    if (!text || loading) return;
    if (!user) { navigate("/login"); return; }
    setLoading(true); setQ("");
    try {
      const { data } = await api.post("/ai/ask", { question: text });
      setHistory((prev) => [...prev, { question: text, answer: data.answer }]);
      if (data.charged) await checkAuth();
      setOneLeft(data.credit?.remaining === 1);
      maybeShow3DayOffer(data.credit);
    } catch (err) {
      handleToolError(err, navigate);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="ai-ask" className="rounded-2xl border-2 border-teal/30 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-5 h-5 text-teal" />
        <h3 className="font-heading font-bold text-navy">Tanya AI Motor Listrik</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-3">Tanya soal harga, spesifikasi, atau penghematan kendaraan di platform ini.</p>
      {history.length > 0 && (
        <div data-testid="ai-history" className="space-y-3 mb-4 max-h-80 overflow-y-auto">
          {history.map((h, i) => (
            <div key={i}>
              <p className="text-sm font-semibold text-navy">{h.question}</p>
              <div className="mt-1 rounded-xl bg-secondary p-3 text-sm text-navy whitespace-pre-wrap" data-testid={i === history.length - 1 ? "ai-ask-answer" : undefined}>{h.answer}</div>
            </div>
          ))}
        </div>
      )}
      {history.length === 0 && !loading && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="ai-quick-questions">
          {QUICK.map((qq, i) => (
            <button
              key={i}
              type="button"
              data-testid={`ai-quick-${i}`}
              onClick={() => submit(qq)}
              className="rounded-full border border-teal/40 bg-teal/5 px-3 py-1.5 text-xs font-medium text-teal transition-colors hover:bg-teal hover:text-white"
            >
              {qq}
            </button>
          ))}
        </div>
      )}
      {oneLeft && (
        <div className="bg-teal/5 border border-teal/20 rounded-xl p-3 mb-3 flex items-center justify-between gap-3 flex-wrap" data-testid="ai-one-left">
          <p className="text-sm text-navy font-medium">Anda masih punya 1 analisis gratis.</p>
          <Button size="sm" data-testid="ai-one-left-cta" onClick={() => navigate("/tanjakan")} className="rounded-full bg-teal hover:bg-teal-hover">Lanjut Cek Motor</Button>
        </div>
      )}
      <div className="flex gap-2">
        <Input data-testid="ai-ask-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit(q)} placeholder="Mis. Motor listrik termurah dengan jarak 60 km?" className="rounded-xl h-11" />
        <Button data-testid="ai-ask-submit" onClick={() => submit(q)} disabled={loading} className="rounded-xl h-11 bg-teal hover:bg-teal-hover px-5">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tanya"}
        </Button>
      </div>
      {user && !isPremium() && <p className="text-xs text-muted-foreground mt-2" data-testid="ai-ask-credits">Jawaban ini menggunakan 1 analisis gratis. {user.trial_credits ?? 0} analisis gratis tersisa.</p>}
      <ThreeDayOfferModal />
      <TrialLimitModal />
    </div>
  );
}
